import type { QueryClient } from '@tanstack/react-query'
import { isSessionDeathCode, isStaleCsrfCode } from './auth-recovery-codes'
import { envVars } from './env-vars'
import { client } from './generated/client.gen'

type GetCsrfToken = (queryClient: QueryClient) => string | null | undefined
type IsSessionAuthenticated = (queryClient: QueryClient) => boolean

interface BrowserSdkConfig {
	queryClient?: QueryClient
	onUnauthorized?: () => Promise<void> | void
	/**
	 * Re-reads the session so the cache holds the current CSRF token.
	 *
	 * Separate from `onUnauthorized`, which clears everything and sends the
	 * person back to the sign-in page: here the session is alive and only the
	 * page's copy of the token is out of date. Unset means a stale token is
	 * never recovered from, which is what every caller did before this
	 * existed.
	 */
	refreshSession?: (client: QueryClient) => Promise<unknown>
	getCsrfToken?: GetCsrfToken
	isSessionAuthenticated?: IsSessionAuthenticated
	requestInterceptorId?: number
	responseInterceptorId?: number
}

interface BrowserSdkConfigOptions {
	queryClient: QueryClient
	onUnauthorized: () => Promise<void> | void
	refreshSession?: (client: QueryClient) => Promise<unknown>
	/**
	 * App-provided reader for the CSRF token; the app knows which query key
	 * holds the session (client vs admin). Returns the token or nullish.
	 * Optional: an app with no session surface has nothing to read it from,
	 * and the request interceptor already skips the header when it is absent.
	 */
	getCsrfToken?: GetCsrfToken
	/**
	 * App-provided reader for the cached session state; the app knows which
	 * query key holds the session (client vs admin). Session recovery fires
	 * only while this is true, so bootstrap 401s (login, MFA challenge,
	 * anonymous session probe) never clear the cache. Optional for the same
	 * reason as `getCsrfToken`: unset means recovery never fires.
	 */
	isSessionAuthenticated?: IsSessionAuthenticated
}

const BROWSER_SDK_CONFIG_KEY = Symbol.for('instance.browser-sdk-config')

type ConfigurableBrowserSdkClient = typeof client & {
	[BROWSER_SDK_CONFIG_KEY]?: BrowserSdkConfig
}

export function configureBrowserSdk({
	queryClient,
	onUnauthorized,
	refreshSession,
	getCsrfToken,
	isSessionAuthenticated
}: BrowserSdkConfigOptions) {
	if (typeof window === 'undefined') {
		return
	}

	const config = getBrowserSdkConfig()
	config.queryClient = queryClient
	config.onUnauthorized = onUnauthorized
	config.refreshSession = refreshSession
	config.getCsrfToken = getCsrfToken
	config.isSessionAuthenticated = isSessionAuthenticated

	client.setConfig({
		baseUrl: resolveBrowserSdkBaseUrl(),
		headers: buildBrowserSdkBaseHeaders(),
		credentials: 'include'
	})

	if (
		config.requestInterceptorId === undefined ||
		!client.interceptors.request.exists(config.requestInterceptorId)
	) {
		config.requestInterceptorId =
			client.interceptors.request.use(addSessionToken)
	}

	if (
		config.responseInterceptorId === undefined ||
		!client.interceptors.response.exists(config.responseInterceptorId)
	) {
		config.responseInterceptorId =
			client.interceptors.response.use(handleRefusal)
	}
}

function getBrowserSdkConfig() {
	const sdkClient = client as ConfigurableBrowserSdkClient
	sdkClient[BROWSER_SDK_CONFIG_KEY] ??= {}
	return sdkClient[BROWSER_SDK_CONFIG_KEY]
}

function readBrowserSdkConfig() {
	return (client as ConfigurableBrowserSdkClient)[BROWSER_SDK_CONFIG_KEY]
}

function addSessionToken(request: Request) {
	const headers = new Headers(request.headers)
	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient
	const token = queryClient ? config?.getCsrfToken?.(queryClient) : undefined

	if (!token) {
		return request
	}

	headers.set('X-CSRF-Token', token)

	return new Request(request, { headers })
}

/**
 * The header a retried request carries, so one retry is one retry.
 *
 * Without it a backend that answers `auth_csrf_invalid` to the fresh token
 * too — a secret rotated under the session, a clock far enough out — would
 * have this interceptor refreshing and retrying for as long as the page is
 * open.
 */
const CSRF_RETRY_HEADER = 'x-csrf-retry'

let sessionRecovery: Promise<void> | undefined
let csrfRefresh: Promise<unknown> | undefined

/**
 * Both recoveries, in the order their causes exclude each other: a dead
 * session is a `401`, a stale CSRF token is a `403`, and no response is both.
 */
async function handleRefusal(response: Response, request: Request) {
	if (response.status === 401) {
		return await handleUnauthorized(response)
	}
	return await handleStaleCsrf(response, request)
}

/**
 * A request refused because its CSRF token belongs to the previous session.
 *
 * ## The failure this closes
 *
 * The token is an HMAC of the session id and the backend checks it on every
 * cookie-based request, `GET` included. A page that is open while the session
 * changes under it — a password reset, a sign-out and back in — keeps the
 * token it read at load, and from that moment **every** call it makes is
 * refused with `403`. Nothing here recovered from that: the recovery beside
 * this one opens with `status !== 401` and returns, because a stale token is
 * not an expired session and must not send somebody back to the sign-in page
 * for it.
 *
 * On staging it was worse than a refusal. CloudFront rewrites a `403` into
 * the app's own `index.html` with a `200`, so the client parsed HTML as JSON
 * and the route's error boundary painted a dead page. That is a second
 * defect, in the distribution rather than here, and this fix does not depend
 * on it: with the token refreshed there is no `403` to rewrite.
 *
 * ## Why it retries, and why only once
 *
 * The call that lost the race is the one the person is waiting on. Refreshing
 * the session and leaving it failed would heal the *next* call and still show
 * this one as an error, which is the behaviour being complained about.
 *
 * A request whose body has already been read cannot be replayed, and
 * `Request` gives no way to get it back. Those are refreshed and not retried:
 * the write fails once, visibly, and the person repeats it on a page that now
 * works. Every read — which is what a page full of listings is made of —
 * replays.
 */
async function handleStaleCsrf(response: Response, request: Request) {
	if (response.status !== 403) {
		return response
	}
	if (request.headers.has(CSRF_RETRY_HEADER)) {
		return response
	}

	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient

	// Only for a session we believe alive, for the same reason the 401
	// recovery checks it: a `403` arriving with no authenticated session is
	// somebody being told no, not a token out of date.
	if (!(queryClient && config.isSessionAuthenticated?.(queryClient))) {
		return response
	}
	if (!config.refreshSession) {
		return response
	}
	if (!isStaleCsrfCode(await readRefusalCode(response))) {
		return response
	}

	// Single-flight: a page that fires eight listings at once produces eight
	// of these, and they want one refresh between them rather than eight.
	// **Never worse than the refusal it is recovering from.** A refresh that
	// rejects — the network gone between the two calls, the session endpoint
	// itself refusing — would otherwise leave this interceptor throwing, and
	// the caller would see that exception in place of the `403` the backend
	// actually sent. The original response is the honest answer when the
	// recovery cannot run.
	try {
		csrfRefresh ??= refreshStaleSession(config, queryClient)
		await csrfRefresh
	} catch {
		return response
	}

	if (request.bodyUsed) {
		return response
	}

	const headers = new Headers(request.headers)
	const token = config.getCsrfToken?.(queryClient)
	if (!token) {
		return response
	}
	headers.set('X-CSRF-Token', token)
	headers.set(CSRF_RETRY_HEADER, '1')

	return await fetch(new Request(request, { headers }))
}

async function refreshStaleSession(
	config: BrowserSdkConfig,
	queryClient: QueryClient
) {
	try {
		return await config.refreshSession?.(queryClient)
	} finally {
		// Cleared here and not by each awaiter, the way `recoverDeadSession`
		// does it: a waiter clearing it in its own `finally` lets the second
		// one start a fresh refresh while the first is still in flight, which
		// is the opposite of single-flight.
		csrfRefresh = undefined
	}
}

async function handleUnauthorized(response: Response) {
	if (response.status !== 401) {
		return response
	}

	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient

	// Recover only when a session we believed alive is declared dead by the
	// backend: bootstrap 401s (login, MFA challenge, anonymous session probe)
	// arrive with a non-authenticated cache and must never clear it.
	if (!(queryClient && config.isSessionAuthenticated?.(queryClient))) {
		return response
	}

	if (!isSessionDeathCode(await readRefusalCode(response))) {
		return response
	}

	// Single-flight: parallel 401s from one dead session share one recovery,
	// and once it clears the cache, stragglers fail the authenticated check.
	sessionRecovery ??= recoverDeadSession(config, queryClient)
	await sessionRecovery

	return response
}

async function recoverDeadSession(
	config: BrowserSdkConfig,
	queryClient: QueryClient
) {
	try {
		queryClient.clear()
		await config.onUnauthorized?.()
	} finally {
		sessionRecovery = undefined
	}
}

async function readRefusalCode(response: Response) {
	try {
		const body: unknown = await response.clone().json()

		if (
			body &&
			typeof body === 'object' &&
			'code' in body &&
			typeof body.code === 'string'
		) {
			return body.code
		}
	} catch {
		// A non-JSON body (a proxy, a CDN rewriting the refusal into a page)
		// is not a verdict this client can read.
	}

	return undefined
}

function resolveBrowserSdkBaseUrl() {
	// Dev: route through the Vite dev-proxy `/api` namespace so backend calls
	// stay same-origin (localhost) and auth cookies work. The proxy strips
	// `/api` before forwarding, so the backend still sees the SDK's bare paths.
	if (import.meta.env.DEV) {
		return '/api'
	}

	return envVars.VITE_API_ENDPOINT
}

function buildBrowserSdkBaseHeaders(): Record<string, string> {
	if (!import.meta.env.DEV) {
		return {}
	}

	return {
		'ngrok-skip-browser-warning': 'true'
	}
}
